import { ExternalLink, FileText, Copy } from 'lucide-react';
import { getBcPostedDocumentUrl, isEosCompany } from '../../utils/bcLinks';
import type { CreditNoteDocument } from '../../types/creditNotes';

/**
 * The three ways out of a credit note, each of which either tells the truth or says nothing.
 *
 * BUSINESS CENTRAL — `getBcPostedDocumentUrl(company, no, 'salescreditmemo')`, page 134.
 * `EOS_CONFIG` in utils/bcLinks.ts is the whole gate: dch, dit, dsp, dfr, dpt and dtr are in it,
 * dbi and bin are not, because those two run on redPoint and have NO web client to link into.
 * No company is named here — if a seventh ever gets a BC tenant, it appears by being added
 * there, and nothing in this file has to change.
 *
 * HUBSPOT — a CUSTOMER link, never a document one. It comes off the row as
 * `hubSpotCustomerUrl`, which the loader reads from `etl.CustomerCRM.HubsportURL` (the column
 * name is misspelled in the replica; that is the ERP's spelling, not ours). No portal id is
 * ever built into this app. `etl.HubspotCustomerLinkERP` looks like the right table for this
 * and is EMPTY — measured, not assumed.
 *
 * DOCUWARE — no link, and that is a finding rather than an omission. See below.
 */

interface Props {
    doc: CreditNoteDocument;
    onCopy?: (value: string) => void;
    /** `icon` for a table cell, `inline` for the drawer header. */
    variant?: 'icon' | 'inline';
}

/**
 * WHY THERE IS NO DOCUWARE LINK — open question 5 of the plan, answered 2026-09-09.
 *
 * The plan expected `DW_Document_No` on the dbi/bin credit-note header to be a DocuWare
 * document id, 99.9 % filled, resolvability untested, and asked P3 to build one URL and click
 * it. Clicking it proves nothing: the URL lands on the DocuWare login page, so a reader without
 * a DocuWare account cannot tell a live document from a dead id. Three measured facts settle it
 * without the click:
 *
 *   1. `DW_Document_No` IS THE CREDIT NOTE'S OWN NUMBER. On 9 of the 10 most recent filled dbi
 *      documents the two columns are identical — 519054 carries `DW_Document_No` 519054. The
 *      tenth, 518796, carries 518165, which is another ERP document. A DocuWare `did` has no
 *      reason to equal an ERP number, and every reason not to.
 *   2. REAL DocuWare IDS ARE AN ORDER OF MAGNITUDE SMALLER. The 13'801 dbi documents in
 *      `[Reporting].[DocuWareDocument]` have DocIds from 137 to 55'298. 519'054 is not in that
 *      id space and cannot become one by waiting.
 *   3. THE ONLY dbi CABINET WE HOLD IS THE INCOMING ONE. `[Reporting].[DocuWareConfig]` has one
 *      dbi cabinet, and `DocuWareDocument` holds INCOMING documents only. An outgoing credit
 *      note is not filed in it, so even a correct id would point at the wrong archive.
 *
 * So the value is shown for what it demonstrably is — an ERP cross-reference — and is not
 * dressed up as an archive link. The amber "unconfirmed" state of DocuWareDocLink exists for a
 * URL we can build but have not verified; this is a URL we have positive evidence AGAINST, and
 * the two must not be shown the same way. A dead link is worse than none, and a link that
 * silently starts hitting an unrelated document once the id spaces overlap is worse than dead.
 */
function DocuWareReference({ doc, onCopy, variant }: Props) {
    if (!doc.dwDocumentNo) return null;
    const title = 'ERP cross-reference (DW_Document_No), not a DocuWare archive id — '
        + 'it repeats the credit note number and falls outside DocuWare’s id range. '
        + 'Outgoing credit notes are not in the archive we hold.';
    return (
        <span className="inline-flex items-center gap-1 text-gray-400" title={title}>
            <FileText className="h-4 w-4" />
            {variant === 'inline' && (
                <button
                    onClick={() => onCopy?.(doc.dwDocumentNo as string)}
                    className="inline-flex items-center gap-1 font-mono text-xs text-gray-500 hover:text-gray-900"
                >
                    DW {doc.dwDocumentNo}
                    <Copy className="h-3 w-3" />
                </button>
            )}
        </span>
    );
}

export function CreditNoteLinks({ doc, onCopy, variant = 'icon' }: Props) {
    const bcUrl = getBcPostedDocumentUrl(doc.companyCode, doc.documentNo, 'salescreditmemo');

    return (
        <span className="inline-flex items-center gap-2">
            {doc.hubSpotCustomerUrl ? (
                <a
                    href={doc.hubSpotCustomerUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={e => e.stopPropagation()}
                    className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-800"
                    title={`Customer ${doc.customerName ?? doc.customerNr ?? ''} in HubSpot`}
                >
                    <ExternalLink className="h-4 w-4" />
                    {variant === 'inline' && <span className="text-xs">Customer in HubSpot</span>}
                </a>
            ) : (
                <span
                    className="inline-flex items-center gap-1 text-gray-300"
                    title="This customer has no HubSpot record in etl.CustomerCRM"
                >
                    <ExternalLink className="h-4 w-4" />
                    {variant === 'inline' && <span className="text-xs text-gray-400">No HubSpot record</span>}
                </span>
            )}

            <DocuWareReference doc={doc} onCopy={onCopy} variant={variant} />

            {bcUrl ? (
                <a
                    href={bcUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={e => e.stopPropagation()}
                    className="text-[11px] font-medium text-blue-600 hover:text-blue-800"
                    title="Open the posted sales credit memo in Business Central"
                >
                    BC
                </a>
            ) : (
                <span
                    className="text-[11px] text-gray-300"
                    title={isEosCompany(doc.companyCode)
                        ? 'No Business Central link could be built for this document'
                        : `${doc.companyCode} runs on redPoint and has no Business Central web client`}
                >
                    BC
                </span>
            )}
        </span>
    );
}
